import React, { useState, useEffect, memo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import LoadingSpinner from "../components/LoadingSpinner";
import api from "../apiReequest";

export default function PartnersPage() {
  const API_BASE = process.env.REACT_APP_API_BASE_URL || '/backend/php';
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [textPrtnrsData, setTextPrtnrsData] = useState(null);
  const [bannerImage, setBannerImage] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  // Fetch partners data
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        setLoading(true);
        const response = await api.get('/partners');
        const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
        setPartners(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching partners:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPartners();
  }, []);

  // Fetch partners page text
  useEffect(() => {
    const fetchTextPrtnrs = async () => {
      try {
        const response = await api.get('/text_prtnrs');
        const data = Array.isArray(response.data) ? response.data[0] : response.data;
        setTextPrtnrsData(data);
      } catch (err) {
        console.error('Error fetching text_prtnrs:', err);
      }
    };
    fetchTextPrtnrs();
  }, []);

  // Fetch banner image - simplified like events/projects
  useEffect(() => {
    const fetchBannerImage = async () => {
      try {
        const response = await api.get('/text_prtnrs?id=2');
        const data = Array.isArray(response.data) ? response.data[0] : response.data;
        if (data && data.image_url) {
          // Resolve the image URL to full path
          const url = String(data.image_url).trim();
          let fullUrl = url;
          if (/^https?:\/\//i.test(url)) {
            fullUrl = url;
          } else if (url.startsWith('/')) {
            // Use API_BASE so remote viewers load from correct backend
            fullUrl = `${API_BASE}${url}`;
          } else if (url.startsWith('api/')) {
            fullUrl = `${API_BASE}/${url}`;
          } else if (!isNaN(Number(url.trim()))) {
            fullUrl = `${API_BASE}/image/${url.trim()}`;
          } else {
            fullUrl = `${API_BASE}/${url}`;
          }
          setBannerImage(fullUrl);
        }
      } catch (err) {
        console.error('Error fetching banner image:', err);
      }
    };
    fetchBannerImage();
  }, [API_BASE]);

  
  const getInitials = (name) => {
    if (!name) return "P";
    return name
      .split(" ")
      .filter(word => !["NGO", "-", "El", "The"].includes(word))
      .map(word => word[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  };

  // Compute a dynamic font-size for partner names: keep short names large,
  // progressively reduce size for longer names so cards keep their shape.
  const computeNameFontSize = (name) => {
    const n = (name || '').trim();
    const len = n.length;
    const base = 18; // default px for short names
    const min = 11;  // minimum px for very long names
    if (len <= 20) return undefined; // use default CSS for short names
    const extra = len - 20; // characters beyond comfortable length
    // reduce at 0.12px per extra char, capped to (base-min)
    const reduction = Math.min(base - min, extra * 0.12);
    const size = Math.max(min, base - reduction);
    return `${Math.round(size * 10) / 10}px`;
  };

  // Helper function to resolve partner image source - SIMPLIFIED
  const resolvePartnerImageSrc = (partner) => {
    if (!partner || !partner.image_url) return null;
    const url = String(partner.image_url).trim();
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith('/')) return `${API_BASE}${url}`;
    if (url.startsWith('api/')) return `${API_BASE}/${url}`;
    if (!isNaN(Number(url.trim()))) return `${API_BASE}/image/${url.trim()}`;
    return `${API_BASE}/${url}`;
  };

  // Small card component to handle per-card image load state and layout
  const PartnerCard = memo(({ partner, idx }) => {
    const [showImage, setShowImage] = React.useState(true);
    
    // Memoize image source - WITHOUT logging
    const imageSrc = React.useMemo(() => {
      const resolvedSrc = resolvePartnerImageSrc(partner);
      return resolvedSrc || null;
    }, [partner]);
    
    const name = partner.name || partner || '';
    const fs = computeNameFontSize(name);

    return (
      <Link key={partner.id || idx} to={`/partners/${partner.id}`} className="block h-full group">
        <motion.div
          className="group relative"
          variants={cardVariants}
          whileHover="hover"
          onHoverStart={() => setHoveredCard(idx)}
          onHoverEnd={() => setHoveredCard(null)}
        >
          <motion.div
            className="absolute -inset-2 bg-gradient-to-br from-primary-400/15 via-emerald-700/15 to-primary-400/15 rounded-3xl blur-lg opacity-0 group-hover:opacity-40"
            style={{ pointerEvents: 'none' }}
            transition={{ duration: 0.12 }}
          />

          <motion.div
            className="relative h-full p-6 pb-4 bg-white/50 backdrop-blur-sm rounded-3xl border border-primary-300/40 hover:border-primary-400/60 shadow-medium cursor-pointer overflow-hidden flex flex-col items-center justify-center min-h-[260px] max-h-[300px]"
            style={{
              background: hoveredCard === idx
                ? "linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.3) 100%)"
                : "linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.2) 100%)"
            }}
          >
            {/* Removed flashing effect */}

            <motion.div
              className="mb-6 relative"
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              {imageSrc && showImage ? (
                <motion.div className="relative w-28 h-28 rounded-full overflow-hidden border-4 border-primary-400/60 shadow-strong">
                  <img
                    key={imageSrc}
                    src={imageSrc}
                    alt={name}
                    className="w-full h-full object-cover"
                    onError={() => setShowImage(false)}
                  />
                </motion.div>
              ) : (
                <motion.div
                  className="w-28 h-28 bg-gradient-to-br from-primary-500 via-emerald-700 to-primary-600 rounded-full flex items-center justify-center shadow-strong relative overflow-hidden"
                >
                  <span className="text-2xl font-bold text-white select-none relative z-10">
                    {getInitials(name)}
                  </span>
                </motion.div>
              )}

            {/* Removed pulsing ring effect */}
            </motion.div>

            <motion.div className="flex-none flex items-center text-center mt-2">
              <h3
                className="partner-name text-slate-900 font-bold leading-tight break-words text-center"
                style={fs ? { fontSize: fs } : undefined}
              >
                {name}
              </h3>
            </motion.div>

            <motion.div
              className="absolute inset-0 bg-gradient-to-br from-emerald-700/5 to-transparent rounded-3xl opacity-0 group-hover:opacity-20"
              style={{ pointerEvents: 'none' }}
              transition={{ duration: 0.12 }}
            />
          </motion.div>
        </motion.div>
      </Link>
    );
  });

  // Dynamic stats
  const filteredPartners = partners;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0,
        duration: 0.1,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 20,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        type: "tween",
        duration: 0.2,
        ease: "easeOut"
      }
    },
    hover: {
      y: -10,
      scale: 1.02,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20
      }
    }
  };

  const glowVariants = {
    initial: { opacity: 0, scale: 0.8 },
    animate: { 
      opacity: [0, 0.6, 0],
      scale: [0.8, 1.2, 0.8],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <div
      className="w-full max-w-8xl rounded-2xl shadow-strong p-2 sm:p-4 md:p-8 text-sm sm:text-base overflow-x-auto"
      style={{
        background: 'rgba(243, 249, 255, 0.9)', // Light slate background
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        boxShadow: '0 10px 40px rgba(15, 23, 42, 0.15)', // Strong professional shadow
      }}
    >
      <div className="relative overflow-hidden partners-page" style={{ paddingBottom: 0 }}>
        {/* Dynamic Gradient Background */}
        <div className="fixed inset-0 bg-slate-50">
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 via-transparent to-transparent"></div>
        </div>

        {/* Animated Background Elements */}
        <div className="fixed inset-0 overflow-hidden">
          {/* Floating Orbs - DISABLED for performance (was causing slow page loads on domain) */}
          {/* 
          <motion.div
            className="absolute w-96 h-96 bg-gradient-to-br from-primary-400/15 to-emerald-700/10 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
            }}
            transition={{
              scale: { duration: 4, repeat: Infinity, ease: "easeInOut" }
            }}
            style={{ top: '10%', left: '10%' }}
          />
          
          <motion.div
            className="absolute w-80 h-80 bg-gradient-to-br from-emerald-700/15 to-primary-400/8 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.1, 1],
            }}
            transition={{
              scale: { duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }
            }}
            style={{ top: '60%', right: '15%' }}
          />
          
          <motion.div
            className="absolute w-72 h-72 bg-gradient-to-br from-primary-500/12 to-emerald-700/12 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.15, 1],
            }}
            transition={{
              scale: { duration: 6, repeat: Infinity, ease: "easeInOut", delay: 2 }
            }}
            style={{ bottom: '20%', left: '30%' }}
          />
          */}

          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-[0.02]">
            <div className="h-full w-full" style={{
              backgroundImage: `
                linear-gradient(rgba(71, 85, 105, 0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(71, 85, 105, 0.1) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px'
            }}></div>
          </div>

          {/* Particle System - disabled for performance */}
        </div>

        {/* Main Content */}
        <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-4 sm:py-8 lg:py-12" style={{ paddingBottom: '0rem' }}>
          <div className="max-w-7xl mx-auto">
            
            {/* Header Section */}
            <motion.div 
              className="text-center mb-8 sm:mb-12 lg:mb-16"
              initial={{ opacity: 0, y: -50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              {/* Animated Icon */}
              <motion.div
                className="relative inline-block mb-6 sm:mb-8"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.1, duration: 0.4, type: "spring", stiffness: 200 }}
              >
                
                
                {/* Pulsing Ring - DISABLED for performance */}
                {/* 
                <motion.div
                  className="absolute inset-0 rounded-full border-2 border-blue-400/30"
                  animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0, 0.3] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                */}
              </motion.div>
              
              {/* Title */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
              >
                <h1 className="text-3xl sm:text-4xl lg:text-7xl font-bold text-slate-900 mb-4 sm:mb-6 tracking-tight leading-tight">
                  Our Strategic
                  <span className="block text-primary-500 mt-2">
                    Partners
                  </span>
                </h1>
              </motion.div>
              

              {/* Partners Introduction Section */}
              <motion.div 
                className="mb-8 sm:mb-12 lg:mb-16 w-full max-w-6xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
              >
                <div className="relative bg-white/95 backdrop-blur-sm rounded-2xl sm:rounded-3xl shadow-xl border border-emerald/20 overflow-hidden px-4 sm:px-8 pb-6 sm:pb-8 p-4 sm:p-6 md:p-8 text-center mx-auto">
                  {/* <h3 className="text-3xl md:text-4xl font-poppins font-bold text-center text-navy mb-6">
                    Our <span className="text-emerald-400">Strategic Partners</span>
                  </h3>
                  
                  <div className="mb-6">
                    <span className="inline-block bg-gradient-to-r from-emerald/15 to-primary/15 rounded-xl px-6 py-4 border border-primary/30">
                      <span className="text-3xl md:text-4xl font-bold text-emerald-400 mr-2 align-middle">∞</span>
                      <span className="text-base md:text-lg text-navy font-medium align-middle">Collaborative Partnerships</span>
                    </span>
                  </div> */}

                  {(() => { 
                    const fallback = '';
                    
                    const rec = textPrtnrsData || null;
                    if (!rec) {
                      return (
                        <p className="text-black text-base sm:text-lg md:text-xl font-roboto leading-relaxed text-center max-w-3xl mx-auto">
                          {fallback}
                        </p>
                      );
                    }
                    const pick = rec.content || rec.text || rec.body || rec.description || rec.value || Object.values(rec).find(v => (typeof v === 'string') && v.trim().length > 0);
                    const textToShow = (typeof pick === 'string' && pick.trim().length > 0) ? pick : fallback;
                    
                    return (
                      <div className="text-center max-w-3xl mx-auto">
                        <p className="text-black text-base sm:text-lg md:text-xl font-roboto leading-relaxed">
                          {textToShow.includes('collaborative') ? (
                            <>
                              Building tomorrow through <span className="font-semibold text-emerald-400">collaborative partnerships</span> that drive{' '}
                              <span className="font-semibold text-primary-500">sustainable economic development</span> and{' '}
                              <span className="font-semibold text-navy">innovation</span> across the region
                            </>
                          ) : (
                            textToShow
                          )}
                        </p>
                        {/* debug buttons removed */}
                      </div>
                    );
                  })()}
                </div>
              </motion.div>

              {/* Partners Banner Image */}
              <div className="relative w-full h-[250px] sm:h-[350px] lg:h-[500px] rounded-2xl sm:rounded-3xl overflow-hidden mb-8 sm:mb-12 lg:mb-16 shadow-2xl flex items-center justify-center">
                {loading ? (
                  <div className="flex items-center justify-center w-full h-full">
                    <LoadingSpinner message="" bgColor="bg-transparent" spinnerColor="border-[#73ab40]" py={0} />
                  </div>
                ) : bannerImage ? (
                  <img
                    src={bannerImage}
                    alt="Partners Banner"
                    className="max-w-full max-h-full object-contain"
                    style={{ objectFit: 'contain', objectPosition: 'center', backgroundColor: 'rgba(0,0,0,0.03)' }}
                    onError={() => setBannerImage(null)}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center w-full h-full">
                    <h3 className="text-2xl sm:text-3xl font-bold">Our Strategic Partners</h3>
                  </div>
                )}
              </div>

              
              {/* Animated Divider */}
              <motion.div
                className="mt-8 flex justify-center"
                initial={{ opacity: 0, scaleX: 0 }}
                animate={{ opacity: 1, scaleX: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
              >
                <motion.div/>
              </motion.div>
            </motion.div>


            {/* Partners Content */}
            <motion.div
              className="relative"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Glassmorphic Container */}
              <div className="relative bg-white/40 backdrop-blur-sm rounded-2xl sm:rounded-3xl border border-slate-200/50 shadow-strong p-4 sm:p-6 lg:p-12 overflow-hidden">
                
                {/* Container Glow Effect */}
                <motion.div
                  className="absolute -inset-1 bg-gradient-to-r from-primary-400/10 via-emerald-700/10 to-primary-400/10 rounded-2xl sm:rounded-3xl blur-xl"
                  variants={glowVariants}
                  initial="initial"
                  animate="animate"
                />
                
                <div className="relative z-10">
                  <AnimatePresence mode="wait">
                    {loading ? (
                      <motion.div
                        key="loading"
                        className="flex justify-center py-24"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.5 }}
                      >
                        <LoadingSpinner message="Loading partners..." bgColor="bg-transparent" spinnerColor="border-[#73ab40]" py={0} />
                      </motion.div>
                    ) : error ? (
                      <motion.div 
                        key="error"
                        className="text-center py-32"
                        initial={{ opacity: 0, scale: 0.8, rotateX: -90 }}
                        animate={{ opacity: 1, scale: 1, rotateX: 0 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.6, type: "spring" }}
                      >
                        <motion.div
                          className="relative w-24 h-24 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-8 backdrop-blur-sm"
                          animate={{ 
                            scale: [1, 1.1, 1],
                            boxShadow: [
                              "0 0 0 0 rgba(59, 130, 246, 0.4)",
                              "0 0 20px 10px rgba(59, 130, 246, 0)",
                              "0 0 0 0 rgba(59, 130, 246, 0.4)"
                            ]
                          }}
                          transition={{ duration: 2, repeat: Infinity }}
                        >
                          <span className="text-4xl">✨</span>
                        </motion.div>
                        
                        <h3 className="text-blue-600 text-2xl mb-6 font-bold">Partners will be available soon. Check back later!</h3>
                        
                        <motion.button 
                          onClick={() => window.location.reload()} 
                          className="group relative px-10 py-4 bg-gradient-to-r from-emerald-700 to-primary-500 text-white rounded-2xl font-semibold transition-all duration-300 overflow-hidden"
                          whileHover={{ 
                            scale: 1.05,
                            boxShadow: "0 20px 40px rgba(59, 139, 201, 0.3)"
                          }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-primary-500 to-emerald-700"
                            initial={{ x: "-100%" }}
                            whileHover={{ x: "0%" }}
                            transition={{ duration: 0.3 }}
                          />
                          <span className="relative z-10">Try Again</span>
                        </motion.button>
                      </motion.div>
                    ) : filteredPartners.length === 0 ? (
                      <motion.div 
                        key="empty"
                        className="text-center py-32"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -50 }}
                        transition={{ duration: 0.6 }}
                      >
                        <motion.div
                          className="w-32 h-32 bg-gradient-to-br from-slate-300/20 to-slate-400/20 rounded-full flex items-center justify-center mx-auto mb-8 backdrop-blur-sm relative overflow-hidden"
                          animate={{ 
                            rotate: [0, 5, -5, 0],
                            scale: [1, 1.05, 1]
                          }}
                          transition={{ 
                            duration: 4, 
                            repeat: Infinity, 
                            ease: "easeInOut" 
                          }}
                        >
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-full"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                          />
                          <span className="text-5xl relative z-10">🔍</span>
                        </motion.div>
                        
                        <motion.h3
                          className="text-slate-900 text-2xl mb-4 font-semibold"
                          animate={{ opacity: [0.7, 1, 0.7] }}
                          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                        >
                          No partners available yet
                        </motion.h3>
                        
                        <motion.p
                          className="text-slate-600 text-lg"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.5 }}
                        >
                          We're constantly building new partnerships
                        </motion.p>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="partners"
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8"
                        variants={containerVariants}
                      >
                        {filteredPartners.sort((a, b) => (a.id || 0) - (b.id || 0)).map((partner, idx) => (
                          <PartnerCard key={partner.id || idx} partner={partner} idx={idx} />
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
            
          </div>
        </div>

        {/* Custom Styles */}
        <style>{`
          .line-clamp-3 {
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          
          .line-clamp-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          
          .animate-float {
            animation: float 6s ease-in-out infinite;
          }
          
          @keyframes shimmer {
            0% { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
          
          .animate-shimmer {
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
            background-size: 200% 100%;
            animation: shimmer 3s infinite;
          }
        `}</style>
        <style>{`
          /* Responsive partner name: always display full name; reduce font-size on smaller screens to avoid layout break */
          .partners-page .partner-name {
            font-size: clamp(12px, 2.2vw, 18px);
            line-height: 1.05;
            text-align: center;
            word-break: break-word;
            white-space: normal;
          }

          /* Reduce name size on medium/desktop-limited widths (e.g., 1280px) */
          @media (max-width: 1280px) {
            .partners-page .partner-name {
              font-size: clamp(11px, 1.6vw, 15px);
            }
          }

          @media (max-width: 1024px) {
            .partners-page .partner-name {
              font-size: clamp(11px, 2.2vw, 14px);
            }
          }

          @media (max-width: 640px) {
            .partners-page .partner-name {
              font-size: clamp(11px, 2.8vw, 14px);
            }
            /* Slightly reduce badge spacing on very small screens */
            .partners-page .group\/badge {
              margin-top: 0.5rem;
            }
          }
        `}</style>
      </div>
    </div>
  );
}